import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import particlesConfig from '../Shared/pr-s-black';
import { GeneralBox } from '../Shared/GeneralBox';
import { API_ENDPOINTS, API_BASE_URL } from '../config/api';
import { useAuth } from '../Contexts/AuthContext';
import { useNotification } from '../Contexts/NotificationContext';


export default function SubscriptionPlans() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [userType, setUserType] = useState('standard');
  const [plansData, setPlansData] = useState({ standard: [], agency: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { showError } = useNotification();

  // Start a real subscription: free plans route into the app; paid plans go
  // through the Zoho hosted checkout (requires sign-in first).
  const handleSubscribe = async (plan) => {
    if (plan.name === 'Free') {
      navigate(user ? '/customer-portal' : '/login');
      return;
    }
    if (!user) {
      navigate('/login');
      return;
    }
    setSubscribing(true);
    try {
      const email = user.email;
      const name = profile?.name || profile?.full_name || (email ? email.split('@')[0] : 'Customer');
      const resp = await fetch(`${API_BASE_URL}/api/subscription/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: plan.id, customer_email: email, customer_name: name, currency: 'CAD' }),
      });
      const result = await resp.json();
      if (result.hosted_page_url) {
        window.location.href = result.hosted_page_url;
      } else {
        showError(result.error?.message || result.error || 'Could not start checkout. Please try again.');
        setSubscribing(false);
      }
    } catch (e) {
      showError('Could not start checkout. Please try again.');
      setSubscribing(false);
    }
  };

  const particlesInit = async (engine) => {
    // you can initiate the tsParticles instance (engine) here, adding custom shapes or presets
    await loadSlim(engine);
  };

  const particlesLoaded = async (container) => {
    // Particles loaded callback
  };

  // Fetch plans from backend on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_ENDPOINTS.plans);
        
        if (!response.ok) {
          throw new Error('Failed to fetch plans');
        }
        
        const data = await response.json();
        
        // Validate response structure and transform
        const standardPlans = Array.isArray(data?.standard) ? data.standard : [];
        const agencyPlans = Array.isArray(data?.agency) ? data.agency : [];
        
        // Transform backend data to match frontend format
        const transformedPlans = {
          standard: standardPlans.map(plan => transformPlan(plan, 'standard')),
          agency: agencyPlans.map(plan => transformPlan(plan, 'agency'))
        };
        
        setPlansData(transformedPlans);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError(err.message);
        setLoading(false);
        // Fallback to empty arrays
        setPlansData({ standard: [], agency: [] });
      }
    };

    fetchPlans();
  }, []);

  // Transform backend plan format to frontend format
  const transformPlan = (plan, planType) => {
    return {
      id: plan.id,
      name: plan.name,
      priceMonthly: plan.priceMonthly,
      priceYearly: plan.priceYearly,
      tokens: plan.tokens,
      description: plan.description,
      features: transformFeatures(plan.features, plan.name, plan.tokens, planType)
    };
  };

  // Transform features from JSONB to frontend array format
  const transformFeatures = (features, planName, tokens, planType) => {
    if (!features) features = {};
    
    const featureList = [];
    
    // Standard (Customer) Plans - show all features from database
    if (planType === 'standard') {
      featureList.push(
        { name: "Profile Groups", included: true },
        { name: "Account Viewers", included: true },
        { name: "Monthly Tokens", included: tokens === null ? "Unlimited" : tokens },
        { name: "Marketing Genius Messages", included: features.marketing_genius_messages || 0 },
        { name: "Report Complexity", included: features.report_complexity || "Basic" },
        { name: "AI Content Generations", included: features.ai_content_generations || 0 },
        { name: "Priority Support", included: features.priority_support || false },
        { name: "Custom Branding", included: features.custom_branding || false }
      );
    } 
    // Agency Plans - show all agency-specific features
    else {
      if (features.team_members !== undefined) {
        featureList.push({ name: "Team Members", included: features.team_members });
      }
      
      featureList.push({ 
        name: "Trend Analytics", 
        included: features.priority_support || false 
      });
      
      featureList.push({ 
        name: "Monthly Tokens", 
        included: tokens === null ? "Unlimited" : tokens 
      });
      
      if (features.marketplace_listings !== undefined) {
        featureList.push({ 
          name: "Marketplace Listings", 
          included: features.marketplace_listings 
        });
      }
      
      if (features.transaction_fee) {
        featureList.push({ 
          name: "Transaction Fees", 
          included: features.transaction_fee 
        });
      }
      
      if (features.client_projects !== undefined) {
        featureList.push({ 
          name: "Client Projects", 
          included: features.client_projects 
        });
      }
      
      if (features.white_label !== undefined) {
        featureList.push({ 
          name: "White Label", 
          included: features.white_label 
        });
      }
      
      if (features.dedicated_account_manager !== undefined) {
        featureList.push({ 
          name: "Account Manager", 
          included: features.dedicated_account_manager 
        });
      }
    }

    return featureList;
  };

  const categoriesStandard = [
    "Profile Groups",
    "Account Viewers",
    "Monthly Tokens"
  ];

  const categoriesAgency = [
    "Team Members",
    "Trend Analytics",
    "Monthly Tokens",
    "Maximum Marketplace Listings",
    "Transaction Fees"
  ];

  const renderCategories = () => {
    const categories = userType === 'standard' ? categoriesStandard : categoriesAgency;
    return categories.map((category, index) => (
      <div key={index} className="category">
        {category}
      </div>
    ));
  };

  const renderPlanFeatures = (features) => {
    return features.map((feature, index) => {
      const isIncluded = feature.included !== false;
      
      // Format the display value
      let displayValue = feature.included;
      if (displayValue === undefined || displayValue === null) {
        displayValue = "";
      }
      if (displayValue === "unlimited" || displayValue === "Unlimited") {
        displayValue = "∞"; // Use infinity symbol instead
      }
      
      
      
      return (
        <div key={index} className="plan-feature-item">
          <span className={isIncluded ? "feature-check" : "feature-x"}>
            {isIncluded ? "✓" : "✕"}
          </span>
          <span className="feature-name">
            {typeof feature.included === 'boolean' || !isIncluded
              ? feature.name 
              : `${feature.name}: ${displayValue}`.replace(/\s+/g, ' ').trim().replace(/\u00A0/g, ' ')}
          </span>
        </div>
      );
    });
  };

  const renderCustomSubscriptionBox = (plan, index) => {
    const price = billingCycle === 'monthly' ? plan.priceMonthly : plan.priceYearly;
    const period = billingCycle === 'monthly' ? '/month' : '/year';
    
    // Get price display text
    const getPriceDisplay = () => {
      if (plan.name === "Free") {
        return "$0 CAD/month";
      }
      // price already includes a "$"; add the CAD unit for clarity.
      const amount = String(price).trim();
      const withSymbol = amount.startsWith('$') ? amount : `$${amount}`;
      return `${withSymbol} CAD${period}`;
    };
    
    // Create custom content for the box
    const customBoxContent = (
      <div className="subscription-box-content">
        <div className="plan-price">
          {getPriceDisplay()}
        </div>
        <div className="plan-features">
          {renderPlanFeatures(plan.features)}
        </div>
        <button
          className="subscribe-button"
          disabled={subscribing}
          onClick={(e) => {
            e.preventDefault();
            handleSubscribe(plan);
          }}
        >
          {subscribing ? 'Starting…' : (plan.name === "Free" ? "Get Started" : "Subscribe")}
        </button>
      </div>
    );
    
    return (
      <div 
        className="subscription-box" 
        key={index} 
        data-plan-name={plan.name.toLowerCase()}
        style={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <GeneralBox
          title={plan.name}
          link="#"
          className="subscription-plan"
          useGlow={true}
          customContent={customBoxContent}
          style={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        />
      </div>
    );
  };

  const activeCategories = userType === 'standard' ? categoriesStandard : categoriesAgency;
  const plans = plansData[userType];

  // Show loading state
  if (loading) {
    return (
      <div className="subscription-page">
        <section className="subscription-header">
          <div className="particles circle-bg">
            <div className="container">
              <div className="title-box">
                <h1>
                  <span className="color-font">CHOOSE YOUR PLAN</span>
                </h1>
              </div>
            </div>
            <Particles id="particles-js" init={particlesInit} loaded={particlesLoaded} options={particlesConfig} />
          </div>
        </section>
        <section className="services">
          <div className="container">
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#fff' }}>
              <p>Loading subscription plans...</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="subscription-page">
        <section className="subscription-header">
          <div className="particles circle-bg">
            <div className="container">
              <div className="title-box">
                <h1>
                  <span className="color-font">CHOOSE YOUR PLAN</span>
                </h1>
              </div>
            </div>
            <Particles id="particles-js" init={particlesInit} loaded={particlesLoaded} options={particlesConfig} />
          </div>
        </section>
        <section className="services">
          <div className="container">
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#ff4444' }}>
              <p>Error loading plans: {error}</p>
              <button 
                onClick={() => window.location.reload()} 
                style={{ 
                  marginTop: '20px', 
                  padding: '10px 20px', 
                  background: '#FE5500', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '5px', 
                  cursor: 'pointer' 
                }}
              >
                Retry
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <section className="subscription-header">
        <div className="particles circle-bg">
          <div className="container">
            <div className="title-box">
              <h1>
                <span className="color-font">CHOOSE YOUR PLAN</span>
              </h1>
            </div>
          </div>
          
          <Particles id="particles-js" init={particlesInit} loaded={particlesLoaded} options={particlesConfig} />
        </div>
      </section>
      
      <section className="services">
        <div className="container">
          <div className="subscription-intro">
            <p>
              Choose the perfect subscription plan tailored to your business needs and growth objectives.
              <span className="highlight"> Organizations with the right subscription tier experience up to 65% higher productivity</span>,
              enabling better resource allocation, improved operational efficiency, and maximized ROI on their digital marketing efforts.
            </p>
          </div>
      
          <div className="pricing-toggles">
            <div className="toggle-container">
              <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={billingCycle === 'yearly'} 
                  onChange={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')} 
                />
                <span className="slider round"></span>
              </label>
              <span className={billingCycle === 'yearly' ? 'active' : ''}>Yearly</span>
              {billingCycle === 'yearly' && (
                <span className="discount-badge">Save 20%</span>
              )}
            </div>
            
            <div className="toggle-container user-type">
              <span className={userType === 'standard' ? 'active' : ''}>Standard</span>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={userType === 'agency'} 
                  onChange={() => setUserType(userType === 'standard' ? 'agency' : 'standard')} 
                />
                <span className="slider round"></span>
              </label>
              <span className={userType === 'agency' ? 'active' : ''}>Agency</span>
            </div>
          </div>

          <div className="subscription-boxes-container">
            {plans.map((plan, index) => renderCustomSubscriptionBox(plan, index))}
          </div>
        </div>
      </section>
    </div>
  );
};
